'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';

const Header = ({ onSearchToggle }) => {
  const pathname = usePathname();
  const { isAuthenticated, userProfile, user } = useAuth();
  const { cartCount } = useCart();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    {
      label: 'Products',
      path: '/product-catalog',
      icon: 'CubeIcon',
      tooltip: 'Browse spare parts catalog',
    },
    {
      label: 'Cart',
      path: '/shopping-cart',
      icon: 'ShoppingCartIcon',
      tooltip: 'View shopping cart',
      badge: cartCount,
    },
    {
      label: 'My Orders',
      path: '/orders',
      icon: 'ClipboardDocumentListIcon',
      tooltip: 'View order history',
    },
  ];

  const isActivePath = (path) => {
    if (path === '/product-catalog' && pathname === '/product-details') return true;
    return pathname === path;
  };

  useEffect(() => {
    if (isMobileMenuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Sticky Premium Navbar */}
      <header className="fixed top-0 left-0 right-0 z-[100]">
        <div className="bg-card/70 backdrop-blur-xl border-b border-border shadow-sm">
          <div className="w-full max-w-7xl mx-auto px-4 lg:px-6">
            <div className="h-16 lg:h-20 flex items-center justify-between gap-4">
              {/* Left: Logo */}
              <Link
                  href="/product-catalog"
                  className="flex items-center gap-3 hover:opacity-90 transition-smooth focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                >
                {/*Bigger Logo + Perfect Fit + Shadow */}
                <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl overflow-hidden bg-muted border border-border flex items-center justify-center shadow-lg shadow-black/10">
                  <AppImage
                    src="/assets/images/athena_earth_movers_logo_unique.png"
                    alt="Athena Earth Movers Logo"
                    width={80}
                    height={80}
                    className="w-full h-full object-cover"
                    priority
                  />
                </div>

                {/* Bigger Company Name */}
                <div className="hidden sm:block leading-tight">
                  <h1 className="text-xl lg:text-2xl font-extrabold text-foreground tracking-tight">
                    Athena Earth Movers <span className="text-[15px] align-super">™</span>
                  </h1>
                  <p className="text-sm text-muted-foreground font-semibold tracking-wide">
                    EXCAVATING EXCELLENCE
                  </p>
                </div>
              </Link>

              {/* Middle: Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-2" aria-label="Primary navigation">
                {/* Links */}
                {navigationItems?.map((item) => (
                  <Link
                    key={item?.path}
                    href={item?.path}
                    className={`relative h-11 px-4 rounded-xl flex items-center gap-2 text-sm font-medium transition-smooth focus:outline-none focus:ring-2 focus:ring-ring ${
                      isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-foreground hover:bg-muted/70'
                    }`}
                    title={item?.tooltip}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span>{item?.label}</span>

                    {item?.badge > 0 && (
                      <span className="ml-1 min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-semibold">
                        {item?.badge > 99 ? '99+' : item?.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </nav>

              {/* Right: Buttons */}
              <div className="hidden lg:flex items-center gap-2">
                {/* Admin */}
                <Link
                  href="/admin-login"
                  className={`h-11 px-4 rounded-xl border border-border flex items-center gap-2 text-sm font-medium transition-smooth focus:outline-none focus:ring-2 focus:ring-ring ${
                    pathname === '/admin-login' || pathname === '/admin-dashboard'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  <Icon name="Cog6ToothIcon" size={18} />
                  <span>Admin</span>
                </Link>

                {/* Account / Login */}
                {isAuthenticated ? (
                  <Link
                    href="/account-information"
                    className="h-11 px-4 rounded-xl bg-foreground text-background hover:opacity-90 transition-smooth flex items-center gap-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <Icon name="UserCircleIcon" size={18} />
                    <span className="max-w-[160px] truncate">
                      {userProfile?.fullName ||
                        userProfile?.full_name ||
                        userProfile?.name ||
                        user?.user_metadata?.fullName ||
                        user?.email?.split('@')?.[0] ||
                        'Account'}
                    </span>
                  </Link>
                ) : (
                  <Link
                    href="/login"
                    className={`h-11 px-4 rounded-xl bg-foreground text-background hover:opacity-90 transition-smooth flex items-center gap-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-ring ${
                      pathname === '/login' ? 'ring-2 ring-ring' : ''
                    }`}
                  >
                    <Icon name="ArrowRightOnRectangleIcon" size={18} />
                    <span>Log In</span>
                  </Link>
                )}
              </div>

              {/* Mobile Right Controls */}
              <div className="flex lg:hidden items-center gap-2">
                {/* Cart */}
                <Link
                  href="/shopping-cart"
                  className="relative h-10 w-10 rounded-xl border border-border bg-background hover:bg-muted flex items-center justify-center transition-smooth"
                  aria-label="Cart"
                >
                  <Icon name="ShoppingCartIcon" size={20} />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-accent text-accent-foreground text-[11px] font-bold">
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </Link>

                {/* Menu */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="h-10 w-10 rounded-xl border border-border bg-background hover:bg-muted flex items-center justify-center transition-smooth"
                  aria-label="Toggle menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[200] lg:hidden bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className="absolute top-0 right-0 h-full w-[85%] max-w-sm bg-card border-l border-border shadow-xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {/*Bigger Mobile Logo */}
                <div className="w-12 h-12 rounded-2xl bg-muted border border-border overflow-hidden flex items-center justify-center shadow-lg shadow-black/10">
                  <AppImage
                    src="/assets/images/athena_earth_movers_logo_unique.png"
                    alt="Athena"
                    width={56}
                    height={56}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="leading-tight">
                  <p className="font-extrabold text-base text-foreground">Athena Earth Movers <span className="text-[12px] align-super">™</span></p>
                  <p className="text-xs text-muted-foreground font-semibold">
                    EXCAVATING EXCELLENCE
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="h-10 w-10 rounded-xl border border-border bg-background hover:bg-muted flex items-center justify-center transition-smooth"
                aria-label="Close menu"
              >
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>

            {/* Links */}
            <div className="flex flex-col gap-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  href={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`h-12 px-4 rounded-xl flex items-center justify-between border border-border transition-smooth ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon name={item?.icon} size={20} />
                    <span className="text-sm font-semibold">{item?.label}</span>
                  </div>

                  {item?.badge > 0 && (
                    <span className="min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-bold">
                      {item?.badge > 99 ? '99+' : item?.badge}
                    </span>
                  )}
                </Link>
              ))}

              <Link
                href="/admin-login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="h-12 px-4 rounded-xl flex items-center gap-3 border border-border bg-background hover:bg-muted transition-smooth"
              >
                <Icon name="Cog6ToothIcon" size={20} />
                <span className="text-sm font-semibold">Admin</span>
              </Link>

              {isAuthenticated ? (
                <Link
                  href="/account-information"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="h-12 px-4 rounded-xl flex items-center gap-3 bg-foreground text-background hover:opacity-90 transition-smooth"
                >
                  <Icon name="UserCircleIcon" size={20} />
                  <span className="text-sm font-semibold">
                    {userProfile?.fullName ||
                      userProfile?.full_name ||
                      userProfile?.name ||
                      user?.user_metadata?.fullName ||
                      user?.email?.split('@')?.[0] ||
                      'Account'}
                  </span>
                </Link>
              ) : (
                <Link
                  href="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="h-12 px-4 rounded-xl flex items-center gap-3 bg-foreground text-background hover:opacity-90 transition-smooth"
                >
                  <Icon name="ArrowRightOnRectangleIcon" size={20} />
                  <span className="text-sm font-semibold">Log In</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
